import { css, html, TemplateResult, svg } from 'lit';
import { MobxLitElement } from '@adobe/lit-mobx';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../styles/shared-styles';
import { type MusicFile } from '../stores/libraryStore';
import { storageService } from '../services/storageService';
import { playbackStore } from '../stores/playbackStore';
import { playIcon } from '../icons/playIcon';

@customElement('album-card')
export class AlbumCard extends MobxLitElement {
    @property({ type: String }) artist = '';
    @property({ type: String }) album = '';
    @property({ type: String }) year = '';
    @property({ type: Array }) files: MusicFile[] = [];
    @property({ type: Object }) albumArt?: MusicFile['albumArt'];
    @property({ type: Object }) albumArtFile?: MusicFile['albumArtFile'];

    @property({ type: String }) albumArtUrl: string | null = null;

    // Disc icon fallback for when no album art is available
    private get discIcon() {
        return svg`
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="album-placeholder-icon">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M6 12c0-1.7.7-3.2 1.8-4.2"></path>
                <circle cx="12" cy="12" r="2"></circle>
                <path d="M18 12c0 1.7-.7 3.2-1.8 4.2"></path>
            </svg>
        `;
    }

    static styles = [styles, css`
        :host {
            display: block;
            width: 200px;
            margin: 16px;
            border-radius: 8px;
            overflow: hidden;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            background: var(--card-bg-color, #fff);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        :host(:hover) {
            transform: translateY(-4px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
            z-index: 1;
        }

        .album-art-container {
            position: relative;
            width: 100%;
            height: 200px;
            overflow: hidden;
        }

        .album-art {
            width: 100%;
            height: 100%;
            object-fit: cover;
            background: var(--album-art-bg, #f0f0f0);
            display: block;
        }

        .album-art-placeholder {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--album-art-bg, #f5f5f5);
            color: var(--text-color-tertiary, #999);
        }

        .album-placeholder-icon {
            opacity: 0.6;
        }

        .play-button {
            position: absolute;
            bottom: 8px;
            right: 8px;
            width: 80px;
            height: 80px;
            background: rgba(0, 0, 0, 0.8);
            border: none;
            border-radius: 50%;
            color: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transform: translateY(10px);
            transition: all 0.2s ease;
            backdrop-filter: blur(4px);
        }

        .play-button:hover {
            background: rgba(0, 0, 0, 0.9);
            transform: translateY(0) scale(1.1);
        }

        .album-art-container:hover .play-button {
            opacity: 1;
            transform: translateY(0);
        }

        .play-button svg {
            width: 40px;
            height: 40px;
            margin-left: 8px; /* Slight offset to visually center the triangle */
        }

        .album-info {
            padding: 12px;
            cursor: pointer;
        }

        .album-name {
            font-size: 14px;
            font-weight: 600;
            color: var(--text-color, #333);
            margin: 0 0 4px 0;
            line-height: 1.3;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            cursor: pointer;
        }

        .artist-name {
            font-size: 12px;
            color: var(--text-color-secondary, #666);
            margin: 0 0 2px 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            cursor: pointer;
        }

        .album-year {
            font-size: 11px;
            color: var(--text-color-tertiary, #999);
            margin: 0;
        }

        .track-count {
            font-size: 10px;
            color: var(--text-color-tertiary, #999);
            margin-top: 4px;
        }

        @media(prefers-color-scheme: dark) {
            :host {
                background: var(--card-bg-color, #2a2a2a);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            }

            :host(:hover) {
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
                z-index: 1;
            }

            .album-art {
                background: var(--album-art-bg, #333);
            }

            .album-art-placeholder {
                background: var(--album-art-bg, #333);
                color: var(--text-color-tertiary, #666);
            }

            .play-button {
                background: rgba(255, 255, 255, 0.9);
                color: #333;
            }

            .play-button:hover {
                background: rgba(255, 255, 255, 1);
            }
        }
    `];

    connectedCallback() {
        super.connectedCallback();
        void this.updateAlbumArtUrl();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanupAlbumArtUrl();
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        super.updated(changedProperties);
        if (changedProperties.has('albumArt') || changedProperties.has('albumArtFile')) {
            void this.updateAlbumArtUrl();
        }
    }

    private async updateAlbumArtUrl() {
        const previousUrl = this.albumArtUrl;

        // load id3 album art data if available
        if (this.albumArt) {
            try {
                const blob = new Blob([this.albumArt.data], {
                    type: this.albumArt.mimeType
                });
                const newUrl = URL.createObjectURL(blob);
                this.albumArtUrl = newUrl;

                if (previousUrl && previousUrl !== newUrl) {
                    URL.revokeObjectURL(previousUrl);
                }

                return;
            } catch (error) {
                console.warn('Error creating album art URL:', error);
            }
        }

        // load album art from file if specified
        if (this.albumArtFile) {
            try {
                const albumArt = await storageService.loadAlbumArt(this.albumArtFile.id);
                // if (requestToken !== this.albumArtRequestToken) {
                //     this.albumArtRequestToken--;
                //     return;
                // }
                if (albumArt) {
                    const blob = new Blob([albumArt.data], { type: albumArt.mimeType });
                    const newUrl = URL.createObjectURL(blob);
                    this.albumArtUrl = newUrl;

                    if (previousUrl && previousUrl !== newUrl) {
                        URL.revokeObjectURL(previousUrl);
                    }
                    return;
                }
            } catch (error) {
                console.warn('Error loading file-based album art:', error);
            }
            return;
        }

        if (previousUrl && this.albumArtUrl === previousUrl) {
            URL.revokeObjectURL(previousUrl);
        }
        this.albumArtUrl = null;
    }

    private cleanupAlbumArtUrl() {
        if (this.albumArtUrl) {
            console.log('Revoking album art URL on cleanup:', this.albumArtUrl);
            URL.revokeObjectURL(this.albumArtUrl);
            this.albumArtUrl = null;
        }
        // if (this.pendingCleanupUrl) {
        //     URL.revokeObjectURL(this.pendingCleanupUrl);
        //     this.pendingCleanupUrl = null;
        // }
    }

    private handleImageLoad() {
        // Clean up any pending URLs after successful load
        // if (this.pendingCleanupUrl) {
        //     URL.revokeObjectURL(this.pendingCleanupUrl);
        //     this.pendingCleanupUrl = null;
        // }
    }

    private handleClick() {
        // This will be used for navigation in the future
        // console.log('Album clicked for navigation:', this.album, 'by', this.artist);
    }

    private handlePlayClick(e: Event) {
        e.stopPropagation(); // Prevent event bubbling to the card click
        // Create playlist from album tracks and start playback
        playbackStore.playAlbum(this.files);
    }

    render(): TemplateResult {
        const trackCount = this.files.length;

        return html`
            <div class="album-card">
                <div class="album-art-container" @click="${this.handleClick}">
                    ${this.albumArtUrl
                ? html`<img
                            class="album-art"
                            src="${this.albumArtUrl}"
                            alt="Album art for ${this.album}"
                            loading="lazy"
                            @load="${this.handleImageLoad}"
                            @error="${(e: Event) => console.error('Album art failed to load:', this.albumArtUrl, e)}"
                        />`
                : html`<div class="album-art-placeholder">
                            ${this.discIcon}
                        </div>`
            }
                    <button
                        class="play-button"
                        @click="${this.handlePlayClick}"
                        aria-label="Play ${this.album} by ${this.artist}"
                    >
                        ${playIcon}
                    </button>
                </div>
                <div class="album-info" @click="${this.handleClick}">
                    <h3 class="album-name" title="${this.album}">${this.album}</h3>
                    <p class="artist-name" title="${this.artist}">${this.artist}</p>
                    ${this.year ? html`<p class="album-year">${this.year}</p>` : ''}
                    <p class="track-count">${trackCount} track${trackCount !== 1 ? 's' : ''}</p>
                </div>
            </div>
        `;
    }
}